"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from "recharts";

export default function MortgageCalculator() {
  const [loanAmount, setLoanAmount] = useState(200000);
  const [interestRate, setInterestRate] = useState(3.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);

  useEffect(() => {
    const calculateMortgage = () => {
      const monthlyRate = interestRate / 100 / 12;
      const numberOfPayments = loanTerm * 12;
      const monthlyPayment =
        (loanAmount *
          monthlyRate *
          // biome-ignore lint/style/useExponentiationOperator: skip
          Math.pow(1 + monthlyRate, numberOfPayments)) /
        // biome-ignore lint/style/useExponentiationOperator: skip
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      const totalPayment = monthlyPayment * numberOfPayments;
      const totalInterest = totalPayment - loanAmount;

      setMonthlyPayment(monthlyPayment);
      setTotalInterest(totalInterest);
      setTotalPayment(totalPayment);
    };

    calculateMortgage();
  }, [loanAmount, interestRate, loanTerm]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const pieChartData = [
    { name: "Principal", value: loanAmount },
    { name: "Interest", value: totalInterest },
  ];

  const COLORS = ["#4CAF50", "#FFA726"];

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          Mortgage Calculator
        </CardTitle>
        <CardDescription className="text-center">
          Adjust the sliders to calculate your mortgage payments
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="loanAmount" className="text-sm font-semibold">
              Loan Amount: {formatCurrency(loanAmount)}
            </Label>
            <Slider
              id="loanAmount"
              min={50000}
              max={1000000}
              step={1000}
              value={[loanAmount]}
              onValueChange={(value) => setLoanAmount(value[0])}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="interestRate" className="text-sm font-semibold">
              Interest Rate: {interestRate.toFixed(2)}%
            </Label>
            <Slider
              id="interestRate"
              min={0.5}
              max={10}
              step={0.1}
              value={[interestRate]}
              onValueChange={(value) => setInterestRate(value[0])}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="loanTerm" className="text-sm font-semibold">
              Loan Term: {loanTerm} years
            </Label>
            <Slider
              id="loanTerm"
              min={5}
              max={30}
              step={1}
              value={[loanTerm]}
              onValueChange={(value) => setLoanTerm(value[0])}
              className="mt-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Monthly Payment</h3>
            <motion.div
              className="text-4xl font-bold text-primary"
              key={monthlyPayment}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {formatCurrency(monthlyPayment)}
            </motion.div>
            <div className="space-y-2">
              <p className="text-sm">
                Total Payment:{" "}
                <span className="font-semibold">
                  {formatCurrency(totalPayment)}
                </span>
              </p>
              <p className="text-sm">
                Total Interest:{" "}
                <span className="font-semibold">
                  {formatCurrency(totalInterest)}
                </span>
              </p>
            </div>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({
                    name,
                    percent,
                  }: { name: string; percent: number }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {pieChartData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
